// react
import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

// internationalization
import { useTranslation } from 'react-i18next';

// tanstack
import { useParams } from '@tanstack/react-router';

// material ui icons
import CalendarTodayRoundedIcon from '@mui/icons-material/CalendarTodayRounded';
import WarningRoundedIcon from '@mui/icons-material/WarningRounded';

// material ui
import {
  Badge,
  Box,
  Button,
  Chip,
  Popover,
  Stack,
  Tooltip,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';

// api
import { useGetTimestampSummaryQuery } from '@/api/energy-analysis/energyDataApiSlice';
import { useGetEnergyGroupQuery } from '@/api/generated/recalc/endpoints/v2/energyGroups';

// relative
import './datepicker.scss';

// feature-components
import TimestampsSummaryWarningRenderer from '@/features/energyxplorer/components/timestamps-summary/TimestampsSummaryWarningRenderer';
import { useTimestampSummaryEvaluation } from '@/features/energyxplorer/components/timestamps-summary/useTimestampSummaryEvaluation';

// feature-utils
import { getRootNode } from '@/features/energyxplorer/utils/energyTreeFunctions';

// components
import { useAlertProvider } from '@/hooks/useAlertProvider';
// components
import useSearchParams from '@/hooks/useStableSearchParams';
import { useUrlDateRange } from '@/hooks/useUrlDateRange';
import { showSnackbar } from '@/redux/snackbarSlice';
import { getTimestampSummaryValidationStatus } from '@/utils/getTimestampSummaryValidationStatus';

// relative
import {
  DatePicker,
  formatDate,
  normalizeRangeToZone,
  shortcutsItems,
  ZONE,
} from './datePickerUtils';
import { DatePickerStyles } from './DateRangePicker';

import { DateTime } from 'luxon';

const DateRangeSelector = ({ toKey, fromKey }) => {
  const { t } = useTranslation('energyxplorer');
  const hasManuallyAdjustedOnce = useRef(true);

  const dispatch = useDispatch();
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down('lg'));

  const { accountId, energyGroupId } = useParams({ strict: false });

  const {
    data: selectedEnergyGroup,
    isLoading: isLoadingEnergyGroup,
    isFetching: isFetchingEnergyGroup,
  } = useGetEnergyGroupQuery(
    { groupId: energyGroupId },
    {
      skip: !energyGroupId,
    },
  );

  //===Time Range & Validity===//
  const {
    currentData: validTimestampSummary,
    isLoading: isLoadingSummary,
    isFetching: isFetchingSummary,
  } = useGetTimestampSummaryQuery(
    {
      accountId: accountId,
      energyGroupId: selectedEnergyGroup?.energyGroupId,
    },
    {
      skip:
        !selectedEnergyGroup?.energyGroupId ||
        !accountId ||
        selectedEnergyGroup?.energyTreeNodes?.length > 250,
    },
  );

  // From hook:
  // - from, to: Luxon DateTime (in zone)
  // - range: [from, to]
  // - timeRange: { start, end } in UTC millis
  const { from, to, range, setRange, timeRange } = useUrlDateRange({
    zone: 'Europe/Copenhagen',
    defaultDays: 30,
    toKey,
    fromKey,
  });

  const rootNode = useMemo(() => {
    if (!selectedEnergyGroup?.energyTreeNodes?.length) return null;
    return getRootNode(selectedEnergyGroup.energyTreeNodes);
  }, [selectedEnergyGroup]);

  const treeNodesLite = useMemo(() => {
    const treeNodes = selectedEnergyGroup?.energyTreeNodes || [];
    return treeNodes.map((treeNode) => ({
      energyTreeNodeId: String(treeNode.energyTreeNodeId),
      parentId: treeNode.parentId === null ? null : String(treeNode.parentId),
      type: treeNode.type,
    }));
  }, [selectedEnergyGroup]);

  const {
    displayWarning: showWarningBadge,
    adjustedRange,
    isSummaryInverted,
    isSummaryNoData,
  } = useTimestampSummaryEvaluation({
    accountId: Number.isFinite(Number(accountId)) ? Number(accountId) : null,
    energyGroupId: Number.isFinite(Number(energyGroupId))
      ? Number(energyGroupId)
      : null,
    scope: 'node',
    compareRange: timeRange,
    treeNodes: treeNodesLite,
    nodeId: rootNode?.energyTreeNodeId
      ? String(rootNode.energyTreeNodeId)
      : null,
    includeAllChildrenInWarning: true,
  });

  // "Adjust time range" is only meaningful when the evaluation produced a
  // usable snap-to-valid-range suggestion (not inverted/no-data).
  const canAdjustToValidRange =
    showWarningBadge &&
    !isSummaryInverted &&
    !isSummaryNoData &&
    !!adjustedRange &&
    !isLoadingSummary &&
    !isFetchingSummary &&
    !isLoadingEnergyGroup &&
    !isFetchingEnergyGroup;

  const handleAdjustToValidRange = () => {
    if (!adjustedRange) return;

    const { start: adjustedStart, end: adjustedEnd } = adjustedRange;
    if (!adjustedStart || !adjustedEnd) return;

    setRange(adjustedStart, adjustedEnd, {
      // Keep exact evaluated bounds; day rounding can re-introduce out-of-bounds.
      roundToDay: false,
      clampToNow: true,
    });
  };

  useAlertProvider({
    enabled: canAdjustToValidRange,
    severity: 'warning',
    message: t('common.timestamp_summary_warning', { ns: 'energyxplorer' }),
    action: (
      <Button color="inherit" size="small" onClick={handleAdjustToValidRange}>
        {t('daterange.adjust_period', { ns: 'energyxplorer' })}
      </Button>
    ),
  });

  // Local picker state: [Date | null, Date | null]
  const [tempStartDate, setTempStartDate] = useState(() =>
    range[0] ? range[0].toJSDate() : null,
  );
  const [tempEndDate, setTempEndDate] = useState(() =>
    range[1] ? range[1].toJSDate() : null,
  );
  const [anchorEl, setAnchorEl] = useState(null);

  // keep in sync when URL range changes
  useEffect(() => {
    const [start, end] = normalizeRangeToZone(range);
    const setTempDate = async () => {
      setTempStartDate(start ? start.toJSDate() : null);
      setTempEndDate(end ? end.toJSDate() : null);
    };

    setTempDate();
  }, [range]);

  const validation = useMemo(() => {
    return getTimestampSummaryValidationStatus(
      timeRange,
      validTimestampSummary,
    );
  }, [timeRange, validTimestampSummary]);

  // when user changes via picker or shortcuts
  const handleDateChange = (dates) => {
    const [start, end] = dates;
    setTempStartDate(start);
    setTempEndDate(end);
  };

  const handleShortcutClick = (shortcut) => {
    const [start, end] = shortcut.getValue();
    setTempStartDate(start.toJSDate());
    setTempEndDate(end.toJSDate());
  };

  const handleConfirm = () => {
    if (!tempStartDate || !tempEndDate) return;

    const start = DateTime.fromJSDate(tempStartDate).setZone(ZONE);
    const end = DateTime.fromJSDate(tempEndDate).setZone(ZONE);

    setRange(start, end, {
      roundToDay: true,
      clampToNow: true,
    });

    setAnchorEl(null);
  };

  const handleClick = (event) => {
    // Re-sync temp dates from current URL when opening
    const [start, end] = range;
    setTempStartDate(start ? start.toJSDate() : null);
    setTempEndDate(end ? end.toJSDate() : null);
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };
  const [searchParams] = useSearchParams();
  const fromParam = searchParams.get('from');
  const toParam = searchParams.get('to');
  const isRangeEmpty = !fromParam && !toParam;
  useEffect(() => {
    if (!accountId || !energyGroupId) return;
    if (hasManuallyAdjustedOnce.current && !isRangeEmpty) return; // ✅ skip after first adjustment

    if (validation.isInverted) {
      hasManuallyAdjustedOnce.current = true;
      return;
    }

    if (validation.isCompletelyInside) {
      hasManuallyAdjustedOnce.current = true;
      return;
    }
    if (validation.isCompletelyOutside) {
      setRange(
        validation.minLatest.minus({ weeks: 4 }).startOf('day'),
        validation.minLatest,
        {
          roundToDay: true,
          clampToNow: true,
        },
      );

      hasManuallyAdjustedOnce.current = true;
      dispatch(
        showSnackbar({
          message: t('daterange.range_adjusted_to_valid_data'),
          severity: 'info',
        }),
      );
      return;
    }

    if (validation.isOutOfBounds && !validation.isCompletelyOutside) {
      hasManuallyAdjustedOnce.current = true;
      setRange(
        validation.adjustedRange[1].minus({ weeks: 4 }).startOf('day'),
        validation.adjustedRange[1],
        {
          roundToDay: true,
          clampToNow: true,
        },
      );
      dispatch(
        showSnackbar({
          message: t('daterange.end_adjusted_to_valid_data'),
          severity: 'info',
        }),
      );
    }
  }, [
    dispatch,
    setRange,
    validation,
    accountId,
    energyGroupId,
    t,
    isRangeEmpty,
  ]);

  useEffect(() => {
    if (!accountId || !energyGroupId) return;
    hasManuallyAdjustedOnce.current = false;
  }, [accountId, energyGroupId]);

  return (
    <>
      <DatePickerStyles theme={theme} />

      <Box display="flex" alignItems="center">
        <Tooltip
          title={t('daterange.select_period')}
          enterDelay={500}
          placement="top"
        >
          <Button
            startIcon={
              <Badge
                overlap="circular"
                badgeContent={
                  showWarningBadge ? (
                    <WarningRoundedIcon fontSize="small" color="warning" />
                  ) : null
                }
              >
                <CalendarTodayRoundedIcon />
              </Badge>
            }
            variant="text"
            onClick={handleClick}
            disableRipple
            sx={{
              ':hover': { background: 'none' },
            }}
          >
            {!isSmall && from && to
              ? `${formatDate(from)} - ${formatDate(to)}`
              : !isSmall
                ? t('daterange.select_period')
                : null}
          </Button>
        </Tooltip>
      </Box>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
        }}
      >
        <Box
          className="custom-datepicker-wrapper"
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
            overflow: 'hidden',
          }}
        >
          {/* Main Content - Shortcuts and Date Picker side by side */}
          <Box sx={{ display: 'flex' }}>
            {/* Shortcuts */}
            <Box
              sx={{
                p: 2,
                minWidth: 160,
              }}
            >
              <Stack direction="column" spacing={1}>
                {shortcutsItems.map((shortcut) => (
                  <Chip
                    key={shortcut.label}
                    label={shortcut.label}
                    onClick={() => handleShortcutClick(shortcut)}
                    size="medium"
                    sx={{
                      cursor: 'pointer',
                      justifyContent: 'flex-start',
                      '&:hover': {
                        backgroundColor: theme.palette.primary.main,
                        color: theme.palette.primary.contrastText,
                      },
                    }}
                  />
                ))}
              </Stack>
            </Box>

            {/* Date Picker */}
            <Box sx={{ p: 2, display: 'flex', justifyContent: 'center' }}>
              <DatePicker
                selected={tempStartDate}
                onChange={handleDateChange}
                startDate={tempStartDate}
                endDate={tempEndDate}
                selectsRange
                inline
                monthsShown={1}
                maxDate={new Date()}
                dateFormat="dd/MM/yyyy"
                showMonthDropdown
                showYearDropdown
                locale={'da'}
              />
            </Box>
          </Box>

          {/* Action Buttons */}
          <Box
            display="flex"
            justifyContent="space-between"
            sx={{
              p: 2,
              backgroundColor: 'background.paper',
            }}
          >
            <Box
              sx={{
                backgroundColor: 'background.paper',
              }}
            >
              {rootNode && (
                <TimestampsSummaryWarningRenderer
                  variant="node"
                  energyGroup={selectedEnergyGroup}
                  node={rootNode}
                  includeAllChildrenInWarning={true}
                />
              )}
            </Box>
            <Button
              onClick={handleConfirm}
              variant="contained"
              sx={{ ml: 1 }}
              disabled={!tempStartDate || !tempEndDate}
            >
              {t('buttonLabel.submit')}
            </Button>
          </Box>
        </Box>
      </Popover>
    </>
  );
};

export default DateRangeSelector;
